import {
  EOR_SUPPORTED_CANDIDATE_COUNTRIES,
  formatEorCandidateNames,
  getEorEligibleCandidates,
  isEorEligibleCandidate,
  isLateStageStep,
} from './eor_utils';
import { Application } from '../../__models__/models';
import { Step } from 'components/job_hiring_steps/models';

function buildApplication(candidateCountryCode: string | null, name = 'Candidate'): Application {
  return {
    attributes: {
      person: {
        attributes: {
          name,
          country_code: candidateCountryCode,
        },
      },
    },
  } as unknown as Application;
}

describe('eor_utils', () => {
  describe('isEorEligibleCandidate', () => {
    it('returns true when a PT candidate applies to a non-PT company', () => {
      expect(isEorEligibleCandidate(buildApplication('PT'), 'ES')).toBe(true);
    });

    it('returns false when a PT candidate applies to a PT company', () => {
      expect(isEorEligibleCandidate(buildApplication('PT'), 'PT')).toBe(false);
    });

    it('returns false for a non-PT candidate', () => {
      expect(isEorEligibleCandidate(buildApplication('ES'), 'UK')).toBe(false);
    });

    it('returns false when company country code is missing', () => {
      expect(isEorEligibleCandidate(buildApplication('PT'), '')).toBe(false);
    });

    it('returns false when candidate country code is missing', () => {
      expect(isEorEligibleCandidate(buildApplication(null), 'ES')).toBe(false);
    });

    it('uses EOR_SUPPORTED_CANDIDATE_COUNTRIES as the source of truth', () => {
      expect(EOR_SUPPORTED_CANDIDATE_COUNTRIES).toContain('PT');
    });
  });

  describe('getEorEligibleCandidates', () => {
    it('filters to only eligible candidates', () => {
      const apps = [
        buildApplication('PT', 'Alice'),
        buildApplication('ES', 'Bob'),
        buildApplication('PT', 'Carol'),
      ];
      const result = getEorEligibleCandidates(apps, 'UK');
      expect(result).toHaveLength(2);
      expect(result.map(a => a.attributes.person.attributes.name)).toEqual(['Alice', 'Carol']);
    });

    it('returns empty when company country code is missing', () => {
      expect(getEorEligibleCandidates([buildApplication('PT')], '')).toEqual([]);
    });

    it('returns empty when there are no eligible candidates', () => {
      expect(getEorEligibleCandidates([buildApplication('ES')], 'UK')).toEqual([]);
    });
  });

  describe('isLateStageStep', () => {
    it('returns true when the step is flagged as late_stage', () => {
      expect(isLateStageStep({ late_stage: true } as unknown as Step)).toBe(true);
    });

    it('returns false when late_stage is false', () => {
      expect(isLateStageStep({ late_stage: false } as unknown as Step)).toBe(false);
    });

    it('returns false when late_stage is undefined', () => {
      expect(isLateStageStep({} as unknown as Step)).toBe(false);
    });
  });

  describe('formatEorCandidateNames', () => {
    it('returns empty string when there are no candidates', () => {
      expect(formatEorCandidateNames([])).toBe('');
    });

    it('returns the single name as-is', () => {
      expect(formatEorCandidateNames([buildApplication('PT', 'Alice')])).toBe('Alice');
    });

    it('joins two names with "and"', () => {
      expect(
        formatEorCandidateNames([
          buildApplication('PT', 'Alice'),
          buildApplication('PT', 'Bob'),
        ])
      ).toBe('Alice and Bob');
    });

    it('shows first two names plus a +N suffix when there are more than two', () => {
      expect(
        formatEorCandidateNames([
          buildApplication('PT', 'Alice'),
          buildApplication('PT', 'Bob'),
          buildApplication('PT', 'Carol'),
          buildApplication('PT', 'Dave'),
        ])
      ).toBe('Alice, Bob +2');
    });
  });
});
